import React, { useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Navbar from '../components/layout/Navbar';
import Icon from '../components/brand/Icon';
import './PaymentLinkPage.css';

const schema = Yup.object({
  amount: Yup.number().positive('Enter a valid amount').required('Amount is required'),
  description: Yup.string().required('Description is required'),
  expiryDays: Yup.number().min(1).max(90).required('Expiry is required'),
});

const PaymentLinkPage = () => {
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);

  const generateLink = (values) => {
    const base = window.location.origin;
    const params = new URLSearchParams({
      amount: values.amount,
      desc: values.description,
      exp: values.expiryDays,
    });
    setLink(`${base}/pay?${params.toString()}`);
    setCopied(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div className="paylink-page">
      <Navbar />
      <div className="paylink-content container">
        <div className="paylink-header">
          <h1>Create Payment Link</h1>
          <p>Generate a shareable link your customers can use to pay you instantly.</p>
        </div>

        <div className="paylink-grid">
          {/* Form */}
          <div className="card paylink-form-card">
            <h2>Link Details</h2>
            <Formik
              initialValues={{ amount: '', description: '', expiryDays: '7' }}
              validationSchema={schema}
              onSubmit={generateLink}
            >
              {({ values }) => (
                <Form>
                  <div className="form-group">
                    <label className="form-label">Amount ($) *</label>
                    <div className="input-prefix-wrap">
                      <span className="input-prefix">$</span>
                      <Field name="amount" type="number" step="0.01" min="0" className="form-input input-with-prefix" placeholder="0.00" />
                    </div>
                    <ErrorMessage name="amount" component="div" className="form-error" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description *</label>
                    <Field name="description" className="form-input" placeholder="e.g. Haircut, Oil change, Delivery" />
                    <ErrorMessage name="description" component="div" className="form-error" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Link expires in</label>
                    <Field as="select" name="expiryDays" className="form-select">
                      <option value="1">1 day</option>
                      <option value="7">7 days</option>
                      <option value="30">30 days</option>
                      <option value="90">90 days</option>
                    </Field>
                  </div>
                  <button type="submit" className="btn btn-primary btn-lg paylink-submit">
                    Generate Payment Link
                  </button>
                </Form>
              )}
            </Formik>
          </div>

          {/* Preview */}
          <div className="card paylink-preview-card">
            <h2>Preview & Share</h2>
            {link ? (
              <div className="paylink-result">
                <div className="qr-wrapper">
                  <QRCodeCanvas value={link} size={160} level="H" />
                </div>
                <p className="qr-hint">Customers can scan this to pay</p>
                <div className="link-display">
                  <span className="link-text">{link}</span>
                </div>
                <div className="paylink-actions">
                  <button className={`btn ${copied ? 'btn-success' : 'btn-primary'}`} onClick={copyLink}>
                    {copied ? 'Copied' : 'Copy Link'}
                  </button>
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline"
                  >
                    Open Link
                  </a>
                </div>
                <div className="paylink-share">
                  <p>Share via:</p>
                  <div className="share-options">
                    <button className="share-btn" onClick={() => window.open(`sms:?body=Pay here: ${link}`)}>
                      <Icon name="link" size={16} /> Text Message
                    </button>
                    <button className="share-btn" onClick={() => window.open(`mailto:?subject=Payment Request&body=Please pay here: ${link}`)}>
                      <Icon name="mail" size={16} /> Email
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon"><Icon name="link" size={30} /></div>
                <h3>No link yet</h3>
                <p>Fill in the form and click Generate to create your payment link.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentLinkPage;
